import { useTranslation } from "react-i18next";
import Field from "../../../ui/forms/Field";
import { FORM_COMPACT_CONTROL_CLASS } from "../../../ui/styles/formStyles";
import BilingualColumns from "./BilingualColumns";
import { columnFallback, columnText, formLanguages, incompleteLanguages, LANGUAGE_NAMES, withItemText, type TextedItem } from "./bilingualTexts";
import FieldProblems from "./FieldProblems";
import type { DefinitionCheck } from "./useDefinitionCheck";

interface ItemTextFieldsProps<T extends TextedItem> {
  item: T;
  nameLabel: string;
  descriptionLabel: string;
  /** The server's problems with the texts, where the item is checked. */
  check: DefinitionCheck | null;
  onChange: (item: T) => void;
}

/**
 * An item's name and description in both languages side by side, the reader's first (WD-1+3 D-9).
 * A blank column shows what its readers see meanwhile; each column shows its own problems.
 */
export default function ItemTextFields<T extends TextedItem>({ item, nameLabel, descriptionLabel, check, onChange }: ItemTextFieldsProps<T>) {
  const { t, i18n } = useTranslation();
  const languages = formLanguages(i18n.resolvedLanguage);
  return (
    <>
      <BilingualColumns languages={languages}>
        {(language, languageName) => (
          <Field label={t("authoring.bilingual.field", { field: nameLabel, language: languageName })}>
            {(control) => (
              <input {...control} lang={language} className={FORM_COMPACT_CONTROL_CLASS} value={columnText(item.translations, language, { field: "name" }, item.name)} placeholder={columnFallback(item.translations, language, { field: "name" }, item.name)} maxLength={200} onChange={(event) => { onChange(withItemText(item, language, "name", event.target.value)); }} />
            )}
          </Field>
        )}
      </BilingualColumns>
      {check === null ? null : <TextProblems check={check} field="name" languages={languages} />}
      <BilingualColumns languages={languages}>
        {(language, languageName) => (
          <Field label={t("authoring.bilingual.field", { field: descriptionLabel, language: languageName })}>
            {(control) => (
              <textarea {...control} lang={language} className={FORM_COMPACT_CONTROL_CLASS} rows={2} value={columnText(item.translations, language, { field: "description" }, item.description)} placeholder={columnFallback(item.translations, language, { field: "description" }, item.description)} onChange={(event) => { onChange(withItemText(item, language, "description", event.target.value)); }} />
            )}
          </Field>
        )}
      </BilingualColumns>
      {check === null ? null : <TextProblems check={check} field="description" languages={languages} />}
      {incompleteLanguages(item.translations).map((language) => (
        <p key={language} className="app-error-text text-xs" role="alert">{t("authoring.bilingual.incomplete", { language: LANGUAGE_NAMES[language] })}</p>
      ))}
    </>
  );
}

/** The problems with one text: the item's own (the English column's) and each language's entry. */
function TextProblems({ check, field, languages }: { check: DefinitionCheck; field: "name" | "description"; languages: readonly string[] }) {
  return (
    <>
      <FieldProblems check={check} path={field} />
      {languages.map((language) => <FieldProblems key={language} check={check} path={`translations.${language}.${field}`} />)}
    </>
  );
}
